// ============================================================
// SCENE FILES - a scene you can carry out of the session.
//
// BY VALUE, NOT BY REFERENCE. A file must open on another machine and
// in another session where the library has neither the map nor the
// characters; a name is not an identity (two "Docks"); and a link would
// make the file depend on state the file cannot see. Loadouts set the
// precedent: templates, not links. `map.name` and each `sheet.id` are
// kept as provenance so a later "update from library" needs no new
// format. The cost is size on disk, which is what disk is for.
//
// The cast rides in the session file's own entry shape and the map in
// the map file's, so nothing here is a third way to spell a combatant or
// a terrain. Pure. Zero React dependency.
// ============================================================
import { packTerrain, unpackTerrain, type MapEntry } from '../battlefield/mapFile';
import { makeToken, type Token } from '../battlefield/tokens';
import { readTeamArrivals, type DeploymentPoint, type TeamArrivals } from '../battlefield/deployment';
import type { TurnTrackerState } from '../../types';
import type { Combatant } from '../roster/types';
import {
  combatantToFile,
  combatantFromFile,
  type CombatantFileEntry,
  type RosterExportOptions,
} from '../roster/sessionFile';
import { normalizeTurnTrackerState } from '../turnTracker';
import { emptyTurnTracker } from '../factories';
import { emptyScene, withTeamPoints } from './park';
import { remintCast, rekeyMarks, rekeyTracker, type Mint } from './mint';
import type { ParkedScene } from './types';
import type { HiddenMark } from '../roster/hidden';
import { PHASES, type Phase } from '../phase/types';
import { remapUnsafeIds } from '../importIds';

/** What every scene file says it is. Anything else is somebody else's file. */
export const SCENE_FILE_KIND = 'deathwatch-scene';
/** Bumped only for a change a reader cannot absorb silently. */
const SCENE_FILE_VERSION = 1;

interface SceneFileCast {
  combatant: CombatantFileEntry;
  /** Where they stand on this map, or null for "ring them at the marker". */
  token: Token | null;
  /**
   * Hidden from the players (2026-10-20): the GM's hidden figure's mark, absent for anyone public. On the ENTRY, not
   * keyed by id, because a file's ids are re-minted on import.
   */
  hidden?: HiddenMark;
}

export interface SceneFile {
  kind: typeof SCENE_FILE_KIND;
  version: number;
  exportedAt?: string;
  scene: {
    name: string;
    notes: string;
    narration: string;
    /** The map file's entry shape, grid packed. */
    map: MapEntry;
    marker: DeploymentPoint | null;
    cast: SceneFileCast[];
    /** Null in a template: a clean scene has no order yet. */
    tracker: TurnTrackerState | null;
    /** "Opens in" (2026-10-20): absent when the scene leaves the table's phase alone. Older files have none. */
    phase?: Phase;
    /** Each team's own arrival point (B): absent when there are none. Older files have none. */
    arrivals?: TeamArrivals;
  };
}

/**
 * A parked scene → the file to hand somebody.
 *
 * `opts.includeState` false is a TEMPLATE: no wounds, no ammo spent, no
 * order - the rehearsal, not the performance. `exportedAt` is passed in
 * rather than read here: this module is pure.
 */
export function toSceneFile(parked: ParkedScene, opts: RosterExportOptions, exportedAt?: string): SceneFile {
  const { record, cast, terrain } = parked;
  return {
    kind: SCENE_FILE_KIND,
    version: SCENE_FILE_VERSION,
    ...(exportedAt ? { exportedAt } : {}),
    scene: {
      name: record.name,
      notes: record.notes,
      narration: record.narration,
      map: { name: record.mapName ?? record.name, terrain: packTerrain(terrain) },
      marker: record.marker,
      cast: cast.map((c) => ({
        combatant: combatantToFile(c, opts),
        token: record.marks[c.id] ?? null,
        ...(record.hidden?.[c.id] ? { hidden: record.hidden[c.id] } : {}),
      })),
      tracker: opts.includeState ? record.tracker : null,
      ...(record.phase ? { phase: record.phase } : {}),
      ...withTeamPoints(record.arrivals),
    },
  };
}

/** A scene as read from a file, before it has ids of its own in a session. */
export interface ReadScene {
  name: string;
  notes: string;
  narration: string;
  map: MapEntry;
  marker: DeploymentPoint | null;
  cast: Array<{ combatant: Combatant; token: Token | null; hidden?: HiddenMark }>;
  tracker: TurnTrackerState;
  /** "Opens in", or null for the table's phase as it is (and for anything this build does not know). */
  phase: Phase | null;
  /** Each team's own arrival point; empty when the file has none. */
  arrivals: TeamArrivals;
}

export interface SceneReadResult {
  scene: ReadScene | null;
  /** What was left out, one sentence each - a person can act on it. */
  warnings: string[];
  /** Why nothing could be read. */
  error: string | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isPlaced = (v: unknown): v is { x: number; z: number } =>
  isRecord(v) && typeof v.x === 'number' && typeof v.z === 'number';
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

const refused = (error: string): SceneReadResult => ({ scene: null, warnings: [], error });

/**
 * A parsed file → the scene, or a reason it cannot be.
 *
 * Never throws. One unreadable cast entry costs one warning, not the
 * scene; a missing map costs the scene, since a stage is nothing without
 * one. Every token passes through `makeToken`, because the token write
 * names every field and erases the ones it cannot find.
 */
export function readSceneFile(data: unknown): SceneReadResult {
  const envelope = sceneEnvelope(remapUnsafeIds(data, castIds(data)));
  if (typeof envelope === 'string') return refused(envelope);
  const { raw, map } = envelope;
  const read = readTerrain(map.terrain);
  if (!read) return refused("That scene file's map could not be read.");
  const { cast, warnings } = readCast(raw.cast);
  return {
    scene: {
      name: raw.name,
      notes: str(raw.notes),
      narration: str(raw.narration),
      map: { name: str(map.name) || raw.name, terrain: read.terrain },
      marker: isPlaced(raw.marker) ? (raw.marker as DeploymentPoint) : null,
      cast,
      tracker: readTracker(raw.tracker),
      phase: PHASES.find((p) => p === raw.phase) ?? null,
      arrivals: readTeamArrivals(raw.arrivals),
    },
    warnings,
    error: null,
  };
}

type Loose = Record<string, unknown>;

/**
 * A scene file's cast entries exactly as stored, unchecked: [] for anything that is not a scene file's shape. For a
 * reader that must NOT go through `readSceneFile` - one keyed on the stored ids, which that read re-mints (the
 * library's team index and its rewrites, plan teams-manager).
 */
function storedCast(data: unknown): unknown[] {
  const cast = isRecord(data) && isRecord(data.scene) ? data.scene.cast : null;
  return Array.isArray(cast) ? cast : [];
}

/** A stored cast entry's combatant, as stored: any record with a string id. */
export type StoredPerson = Loose & { id: string };

const personOf = (entry: unknown): StoredPerson | null =>
  isRecord(entry) && isRecord(entry.combatant) && typeof entry.combatant.id === 'string'
    ? (entry.combatant as StoredPerson)
    : null;

/** The people in a stored scene file, keyed by their stored ids; [] for anything that is not one. */
export function storedPeople(data: unknown): StoredPerson[] {
  return storedCast(data).flatMap((entry) => personOf(entry) ?? []);
}

/**
 * The stored scene file with each person passed through `edit` - handing the same object back leaves its entry as it
 * was. Never mutates; shares every value it does not change. Refuses, with a sentence, anything that is not a scene
 * file, so a rewrite can never turn some other payload into a scene.
 */
export function mapStoredPeople(data: unknown, edit: (person: StoredPerson) => StoredPerson): unknown {
  if (!isRecord(data) || data.kind !== SCENE_FILE_KIND || !isRecord(data.scene)) {
    throw new Error('That is not a stored scene file.');
  }
  const cast = storedCast(data).map((entry) => {
    const person = personOf(entry);
    const next = person && edit(person);
    return next && next !== person ? { ...(entry as Loose), combatant: next } : entry;
  });
  return { ...data, scene: { ...data.scene, cast } };
}

/**
 * The ids a scene file's cast carries: each combatant's (re-minted on
 * import, but the tracker and marks name them) and each sheet's, which is
 * KEPT and names `characters/{id}` when the sheet is saved to the library.
 * Checked before anything reads them - see importIds.
 */
function castIds(data: unknown): unknown[] {
  const people = storedCast(data).map((e) => (isRecord(e) && isRecord(e.combatant) ? e.combatant : {}));
  return people.flatMap((c) => [c.id, isRecord(c.sheet) ? c.sheet.id : undefined]);
}

/** The scene and its map, or the sentence that says why the file is not one. */
function sceneEnvelope(data: unknown): { raw: Loose & { name: string }; map: Loose & { terrain: Loose } } | string {
  if (!isRecord(data)) return 'That file does not contain a scene.';
  if (data.kind !== SCENE_FILE_KIND) {
    return 'That is not a Deathwatch scene file. A map goes through Import maps, a session through Import Session.';
  }
  const raw = data.scene;
  if (!isRecord(raw) || typeof raw.name !== 'string' || !raw.name) return 'That scene file has no scene in it.';
  const map = raw.map;
  if (!isRecord(map) || !isRecord(map.terrain)) return 'That scene file has no map.';
  return { raw: raw as Loose & { name: string }, map: map as Loose & { terrain: Loose } };
}

/**
 * The map's terrain, or null when it cannot be read. Through unknown: the
 * reader has established an object, not a Terrain, and unpackTerrain
 * tolerates older shapes itself.
 */
function readTerrain(stored: Loose): { terrain: MapEntry['terrain'] } | null {
  try {
    return { terrain: unpackTerrain(stored as unknown as MapEntry['terrain']) };
  } catch {
    return null;
  }
}

/** Every readable cast entry, with a warning for each one left out. */
function readCast(stored: unknown): { cast: ReadScene['cast']; warnings: string[] } {
  const warnings: string[] = [];
  const cast: ReadScene['cast'] = [];
  const entries = Array.isArray(stored) ? stored : [];
  entries.forEach((entry, i) => {
    const read = isRecord(entry) ? castEntryOf(entry, i) : null;
    if (read) cast.push(read);
    else warnings.push(`Cast entry ${i + 1} could not be read and was left out.`);
  });
  return { cast, warnings };
}

/** One cast entry: the person, where they stand (or null), and their hidden mark when they are hidden; null if unread. */
function castEntryOf(entry: Loose, i: number): ReadScene['cast'][number] | null {
  const combatant = combatantFromFile(entry.combatant, i);
  if (!combatant) return null;
  const token = isPlaced(entry.token) ? makeToken(combatant.id, { ...entry.token, id: combatant.id }) : null;
  const hidden = hiddenMarkOf(entry.hidden);
  return { combatant, token, ...(hidden ? { hidden } : {}) };
}

/** A whole hidden mark, or null: a torn one hides nobody (the GM can hide them again at the table). */
function hiddenMarkOf(raw: unknown): HiddenMark | null {
  if (!isRecord(raw) || typeof raw.alias !== 'string' || typeof raw.bornHidden !== 'boolean') return null;
  const initiative = isRecord(raw.initiative) ? (raw.initiative as unknown as HiddenMark['initiative']) : null;
  return { alias: raw.alias, initiative, bornHidden: raw.bornHidden };
}

/** The saved initiative, or an empty one when it is missing or malformed. */
function readTracker(stored: unknown): TurnTrackerState {
  const normalized = normalizeTurnTrackerState((stored as TurnTrackerState | null) ?? null);
  return normalized && Array.isArray(normalized.participants) ? normalized : emptyTurnTracker();
}

/**
 * A read scene → a scene in the wings, with ids of its own.
 *
 * NEW IDS FOR EVERYTHING, so a file imported twice is two scenes and no
 * id in a file can ever collide with a live person. `sheet.id` is kept -
 * it is what identity elsewhere matches on - and was checked at the read.
 * Without a marker of its own the scene takes the map's suggestion, as
 * `emptyScene` does.
 */
export function sceneFromFile(read: ReadScene, mint: Mint, now: number, order: number): ParkedScene {
  const base = emptyScene(mint('scene'), read.name, read.map.terrain, read.map.name, order, now);
  const { cast, idMap } = remintCast(
    read.cast.map((e) => e.combatant),
    mint,
  );
  const marks: Record<string, Token> = {};
  for (const e of read.cast) if (e.token) marks[e.combatant.id] = e.token;
  // Who is hidden, under the ids they have now.
  const hidden = Object.fromEntries(
    read.cast.filter((e) => e.hidden).map((e) => [idMap[e.combatant.id] ?? e.combatant.id, e.hidden as HiddenMark]),
  );
  return {
    record: {
      ...base,
      notes: read.notes,
      narration: read.narration,
      marker: read.marker ?? base.marker,
      phase: read.phase,
      ...withTeamPoints(read.arrivals),
      tracker: rekeyTracker(read.tracker, idMap),
      marks: rekeyMarks(marks, idMap),
      castIds: cast.map((c) => c.id),
      hidden,
    },
    cast,
    terrain: read.map.terrain,
  };
}
